<template>
    <main class="xigcheck-home-mobile">
        <!-- Hero Section -->
        <section class="hero-section">
            <div class="eyebrow-wrapper">
                <span class="eyebrow">XIG CHECK</span>
            </div>
            <h1>
                Independent Product
                <br />
                Review &amp; Marketplace
                <br />
                <span class="title-line">Trust Layer</span>
            </h1>
            <p class="hero-desc">XigCheck is the dedicated product review and marketplace trust support layer affiliated with the XigFor marketplace platform. Built for trust, transparency, and marketplace integrity.</p>

            <div class="hero-visual">
                <img src="@/assets/xigcheck/mobile-home.png" alt="Luxury goods reviewed by XigCheck" class="hero-product" />
            </div>

            <div class="hero-features">
                <div v-for="feature in heroFeatures" :key="feature.label" class="hero-feature">
                    <img :src="feature.icon" :alt="feature.label.replace('<br />', ' ')" />
                    <span v-html="feature.label"></span>
                </div>
            </div>

            <div class="hero-actions">
                <router-link to="/xigcheck/how-it-works" class="primary-btn">Learn How It Works</router-link>
                <router-link to="/xigcheck/trust-layer" class="outline-btn">Explore Trust Layer</router-link>
            </div>

            <div class="hero-note">
                <svg class="shield-check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 11 2 2 4-4" />
                </svg>
                <span>Supporting selected high-value second-hand product categories.</span>
            </div>
        </section>

        <!-- Trust Cards Section -->
        <section class="trust-cards-section">
            <div v-for="item in trustItems" :key="item.title" class="trust-card">
                <div class="round-icon">
                    <img :src="item.icon" :alt="item.title" />
                </div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.text }}</p>
            </div>
        </section>

        <!-- How It Works Section -->
        <section class="works-section">
            <div class="section-heading">
                <h2>How XigCheck Works</h2>
                <p>A simple and transparent review process to support trust for high-value products.</p>
            </div>
            <div class="steps-column">
                <div v-for="(step, index) in steps" :key="step.title" class="step-card-mobile">
                    <div class="step-icon-wrapper">
                        <div class="step-icon">
                            <img :src="step.icon" :alt="step.title" />
                        </div>
                        <div v-if="index < steps.length - 1" class="step-connector"></div>
                    </div>
                    <div class="step-content">
                        <strong>{{ step.number }}</strong>
                        <h3>{{ step.title }}</h3>
                        <p>{{ step.text }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Supported Categories Section -->
        <section class="categories-section">
            <div class="section-heading">
                <h2>Supported Categories</h2>
                <p>XigCheck provides review support for selected high-value and collectible categories.</p>
            </div>
            <div class="category-grid">
                <article v-for="category in categories" :key="category.title" class="category-card">
                    <div class="mini-search"><img :src="icons.search" alt="" /></div>
                    <img :src="category.image" :alt="category.title.replace('<br />', ' ')" class="category-image" />
                    <h3 v-html="category.title"></h3>
                    <div class="lines-wrapper">
                        <p v-for="line in category.lines" :key="line">{{ line }}</p>
                    </div>
                </article>
            </div>
            <router-link to="/xigcheck/supported-categories" class="text-link">View all supported categories <span class="arrow">&gt;</span></router-link>
        </section>

        <!-- Brands Section -->
        <section class="brands-section">
            <div class="section-heading">
                <h2>Brands Commonly Reviewed</h2>
                <p>Examples of brands that may appear within our review categories.</p>
            </div>
            <div class="brand-grid">
                <div v-for="brand in brands" :key="brand.name" class="brand-card">
                    <img :src="brand.image" :alt="brand.name" />
                </div>
            </div>
            <div class="brand-note">
                Brand references are provided solely to identify categories of products that may be reviewed.<br />
                XigCheck is not affiliated with, endorsed by, or officially authorized by the listed brands unless explicitly stated otherwise.
            </div>
            <router-link to="/xigcheck/brand-categories" class="text-link">View Full Brand List &amp; Guidelines <span class="arrow">&gt;</span></router-link>
        </section>

        <!-- Compliance & Protection Section -->
        <section class="compliance-section">
            <div class="compliance-inner">
                <div class="compliance-intro">
                    <h2>Compliance &amp; Protection</h2>
                    <p>XigCheck is designed to support marketplace trust and operate in accordance with strict compliance and protection standards.</p>
                    <router-link to="/xigcheck/compliance-protection" class="primary-btn">Learn More</router-link>
                </div>
                <div class="compliance-cards-stack">
                    <router-link v-for="card in complianceCards" :key="card.title" :to="card.route" class="compliance-card">
                        <div class="card-top">
                            <img :src="card.icon" :alt="card.title" />
                            <h3>{{ card.title }}</h3>
                        </div>
                        <p>{{ card.text }}</p>
                        <span>Learn More &gt;</span>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- Related Policies Section -->
        <section class="related-section">
            <strong>Related Policies</strong>
            <div class="policy-links">
                <router-link v-for="policy in relatedPolicies" :key="policy.label" :to="policy.route" class="policy-link-item">
                    <div class="policy-left">
                        <img :src="policy.icon" alt="" />
                        <span>{{ policy.label }}</span>
                    </div>
                    <span class="chevron">&gt;</span>
                </router-link>
            </div>
        </section>
    </main>
</template>

<script>
import check2Icon from '@/assets/xigcheck/check2.png'
import compassIcon from '@/assets/xigcheck/upde.png'
import dollarIcon from '@/assets/xigcheck/currency-coin-dollar.png'
import cardIcon from '@/assets/xigcheck/card-02.png'
import lockIcon from '@/assets/xigcheck/lock.png'
import lockOpenIcon from '@/assets/xigcheck/lock-open-02.png'
import searchIcon from '@/assets/xigcheck/search-02.png'
import userGroupIcon from '@/assets/xigcheck/user-profile-group.png'
import vectorIcon from '@/assets/xigcheck/Vector.png'
import iconImage from '@/assets/xigcheck/zfx.png'
import icon2Image from '@/assets/xigcheck/Icon2.png'
import shieldCheckIcon from '@/assets/xigcheck/sasety.png'
import luxuryAccessories from '@/assets/xigcheck/Luxury Accessories.png'
import luxuryEyewear from '@/assets/xigcheck/Luxury Eyewear.png'
import luxuryFashion from '@/assets/xigcheck/Luxury Fashion & Apparel.png'
import premiumSneakers from '@/assets/xigcheck/Premium Sneakers.png'
import homeGoods from '@/assets/xigcheck/Home & Lifestyle Goods.png'
import homeGoodsOne from '@/assets/xigcheck/Home & Lifestyle Goods（1）.png'
import homeGoodsTwo from '@/assets/xigcheck/Home & Lifestyle Goods（2）.png'
import louisVuitton from '@/assets/xigcheck/Louis Vuitton.png'
import bag from '@/assets/xigcheck/image_11.png'
import chanel from '@/assets/xigcheck/Chanel.png'
import hermes from '@/assets/xigcheck/Hermes.png'
import gucci from '@/assets/xigcheck/Gucci.png'
import rolex from '@/assets/xigcheck/Rolex.png'
import nike from '@/assets/xigcheck/Nike.png'
import adidas from '@/assets/xigcheck/Adidas.png'
import dior from '@/assets/xigcheck/Dior.png'
import prada from '@/assets/xigcheck/Prada.png'
import cartier from '@/assets/xigcheck/Cartier.png'

import trust1 from '@/assets/xigcheck/sasety.png'
import trust2 from '@/assets/xigcheck/user.png'
import trust3 from '@/assets/xigcheck/lock-02.png'
import svgMargin from '@/assets/xigcheck/SVG_margin.png'
import linkIcon from '@/assets/xigcheck/link.png'

import compass from '@/assets/xigcheck/compass.png'
export default {
    name: 'XigCheckHomeMobile',
    data() {
        return {
            icons: {
                check2: check2Icon,
                search: searchIcon
            },
            heroFeatures: [
                { label: 'AI-Assisted<br />Review', icon: compass },
                { label: 'Expert<br />Assessment', icon: searchIcon },
                { label: 'Third-Party<br />Support', icon: userGroupIcon }
            ],
            trustItems: [
                {
                    title: 'Marketplace Trust',
                    text: 'Supporting transparency and marketplace integrity for high-value second-hand products.',
                    icon: trust1
                },
                {
                    title: 'Independent Review',
                    text: 'Combining AI technology, expert assessment, and third-party authentication support.',
                    icon: trust2
                },
                {
                    title: 'Fraud Prevention',
                    text: 'Helping reduce counterfeit risks and suspicious activities through multi-layer review procedures.',
                    icon: trust3
                }
            ],
            steps: [
                {
                    number: '01',
                    title: 'Submit Product',
                    text: 'Users submit product images and listing details.',
                    icon: vectorIcon
                },
                {
                    number: '02',
                    title: 'Review Process',
                    text: 'AI-assisted analysis, expert assessment, and third-party review support.',
                    icon: searchIcon
                },
                {
                    number: '03',
                    title: 'Review Outcome',
                    text: 'Review outcomes represent independent assessment opinions only.',
                    icon: shieldCheckIcon
                }
            ],
            categories: [
                {
                    title: 'Luxury<br />Handbags',
                    image: bag,
                    lines: ['designer handbags', 'Leather handbags', 'Limited edition', 'Collectible handbags']
                },
                {
                    title: 'Luxury<br />Accessories',
                    image: luxuryAccessories,
                    lines: ['Wallets', 'Belts', 'Scarves', 'fashion accessories', 'Small leather goods']
                },
                {
                    title: 'Premium<br />Sneakers',
                    image: premiumSneakers,
                    lines: ['Premium sneakers', 'Collectible sneakers', 'Designer footwear', 'Limited-release footwear']
                },
                {
                    title: 'Luxury Fashion<br />& Apparel',
                    image: luxuryFashion,
                    lines: ['Luxury clothing', 'Collectible streetwear', 'Designer outerwear']
                },
                {
                    title: 'Luxury<br />Eyewear',
                    image: luxuryEyewear,
                    lines: ['Sunglasses', 'Designer eyewear', 'Collectible eyewear']
                },
                {
                    title: 'Luxury<br />Watches',
                    image: homeGoodsTwo,
                    lines: ['Luxury Timepieces', 'Sports & Chronograph Watches', 'Vintage & Pocket Watches']
                },
                {
                    title: 'Fine Jewelry<br />& Accessories',
                    image: homeGoodsOne,
                    lines: ['Luxury rings & bands', 'Fine necklaces', 'Gemstone jewelry', 'Collectible pieces']
                },
                {
                    title: 'Home<br />& Lifestyle Goods',
                    image: homeGoods,
                    lines: ['selected home accessories', 'selected decorative goods']
                }
            ],
            brands: [
                { name: 'Louis Vuitton', image: louisVuitton },
                { name: 'Chanel', image: chanel },
                { name: 'Hermes', image: hermes },
                { name: 'Gucci', image: gucci },
                { name: 'Rolex', image: rolex },
                { name: 'Nike', image: nike },
                { name: 'Adidas', image: adidas },
                { name: 'Dior', image: dior },
                { name: 'Prada', image: prada },
                { name: 'Cartier', image: cartier }
            ],
            complianceCards: [
                {
                    title: 'Fraud Prevention',
                    text: 'Proactive risk control and counterfeit prevention systems.',
                    route: '/xigcheck/fraud-prevention',
                    icon: lockIcon
                },
                {
                    title: 'Independent Review',
                    text: 'Structured review procedures by experts and partners.',
                    route: '/xigcheck/independent-review',
                    icon: shieldCheckIcon
                },
                {
                    title: 'No Escrow',
                    text: 'We do not hold or custody user funds.',
                    route: '/xigcheck/no-escrow',
                    icon: cardIcon
                },
                {
                    title: 'No Financial Services',
                    text: 'XigCheck does not provide financial, insurance, or investment services.',
                    route: '/xigcheck/no-financial-services',
                    icon: dollarIcon
                }
            ],
            relatedPolicies: [
                { label: 'Terms of Service', route: '/xigcheck/terms-of-service', icon: icon2Image },
                { label: 'Privacy Policy', route: '/xigcheck/privacy-policy', icon: lockOpenIcon },
                { label: 'Refund Policy', route: '/xigcheck/refund-policy', icon: compassIcon },
                { label: 'Authentication Process Policy', route: '/xigcheck/authentication-process-policy', icon: svgMargin },
                { label: 'Fraud Prevention Policy', route: '/xigcheck/fraud-prevention-policy', icon: linkIcon },
                { label: 'High-value Goods Policy', route: '/xigcheck/high-value-goods-policy', icon: iconImage }
            ]
        }
    }
}
</script>

<style scoped>
.xigcheck-home-mobile {
    width: 100%;
    color: #252633;
    background: #fff;
    font-family: 'Poppins', Arial, sans-serif;
    padding: 0;
    box-sizing: border-box;
    overflow-x: hidden;
}

/* Hero Section */
.hero-section {
    background: radial-gradient(circle at 0% 0%, #ffe6ee 0%, rgba(255, 255, 255, 0) 70%), linear-gradient(180deg, #fff0f5 0%, #ffffff 100%);
    padding: 32px 20px;
    text-align: left;
    box-sizing: border-box;
}
.eyebrow-wrapper {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
}
.eyebrow {
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding: 0 16px;
    border-radius: 99px;
    background: #fdeaf1;
    color: #f43f5e;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.05em;
}
.hero-section h1 {
    margin: 0 0 16px 0;
    font-size: 30px;
    line-height: 1.25;
    font-weight: 800;
    color: #171927;
}
.title-line {
    color: #ff4569;
}
.hero-desc {
    margin: 0 0 24px 0;
    color: #5f6472;
    font-size: 13px;
    line-height: 1.6;
    font-weight: 500;
    padding: 0 16px 0 0;
}
.hero-visual {
    margin: 0 auto 24px auto;
    max-width: 100%;
    display: flex;
    justify-content: center;
}
.hero-product {
    width: 100%;
    max-width: 380px;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 12px 24px rgba(88, 57, 64, 0.06));
}
.hero-features {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 28px;
    padding: 0;
    gap: 0;
}
.hero-feature {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    flex: 1;
    position: relative;
    box-sizing: border-box;
}
.hero-feature:first-child {
    padding-right: 16px;
}
.hero-feature:nth-child(2) {
    padding-left: 16px;
    padding-right: 16px;
}
.hero-feature:last-child {
    padding-left: 16px;
}
.hero-feature img {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
    object-fit: contain;
}
.hero-feature span {
    font-size: 11px;
    font-weight: 700;
    color: #252633;
    line-height: 1.3;
    display: block;
}
.hero-feature:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    width: 1px;
    height: 30px;
    background-color: #e5e7eb;
}
.hero-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
    width: 100%;
    padding: 0;
    box-sizing: border-box;
}
.primary-btn,
.outline-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    width: 100%;
    box-sizing: border-box;
    transition: all 0.2s ease;
}
.primary-btn {
    background: #ff4569;
    color: #fff;
    box-shadow: 0 6px 16px rgba(255, 69, 105, 0.2);
}
.outline-btn {
    border: 1.5px solid #ff4569;
    color: #ff4569;
    background: transparent;
}
.hero-note {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #858895;
    font-size: 11px;
    line-height: 1.4;
    text-align: left;
    margin-top: 16px;
    justify-content: flex-start;
    padding: 0;
}
.shield-check-icon {
    width: 16px;
    height: 16px;
    stroke: #ff4569;
    flex-shrink: 0;
}

/* Trust Cards Section */
.trust-cards-section {
    padding: 32px 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #fff;
}
.trust-card {
    background: #fff;
    border: 1px solid #f0f2f5;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.trust-card .round-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #fff0f5;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}
.trust-card .round-icon img {
    width: 26px;
    height: 26px;
    object-fit: contain;
}
.trust-card h3 {
    font-size: 16px;
    font-weight: 700;
    color: #252633;
    margin: 0 0 8px 0;
}
.trust-card p {
    font-size: 12px;
    color: #777b88;
    line-height: 1.6;
    margin: 0;
}

/* How It Works Section */
.works-section {
    background: #f7f8fa;
    padding: 40px 20px;
}
.section-heading h2 {
    font-size: 22px;
    font-weight: 800;
    color: #202230;
    margin: 0 0 10px 0;
    text-align: center;
}
.section-heading p {
    font-size: 13px;
    color: #5f6472;
    margin: 0 0 32px 0;
    text-align: center;
    line-height: 1.5;
}
.steps-column {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0 10px;
}
.step-card-mobile {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}
.step-icon-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    align-self: stretch;
}
.step-icon-wrapper .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fff0f5;
    border: 2px solid #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.step-icon-wrapper .step-icon img {
    width: 18px;
    height: 18px;
    object-fit: contain;
}
.step-connector {
    width: 2px;
    flex-grow: 1;
    border-left: 2px dotted #ff9caf;
    margin-top: 4px;
    margin-bottom: 4px;
    z-index: 1;
}
.step-content {
    padding-top: 2px;
    padding-bottom: 28px;
    flex: 1;
}
.step-content strong {
    font-size: 12px;
    color: #ff4569;
    font-weight: 700;
    display: block;
    margin-bottom: 4px;
}
.step-content h3 {
    font-size: 16px;
    font-weight: 700;
    color: #202230;
    margin: 0 0 6px 0;
}
.step-content p {
    font-size: 12px;
    color: #777b88;
    line-height: 1.5;
    margin: 0;
}

/* Supported Categories */
.categories-section {
    padding: 40px 20px;
    background: #fff;
}
.category-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 24px;
}
.category-card {
    position: relative;
    border: 1px solid #eef1f6;
    border-radius: 16px;
    background: #fff;
    padding: 16px 8px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.category-card:active {
    transform: scale(0.97);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
}
.category-card .mini-search {
    position: absolute;
    left: 10px;
    top: 10px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff0f5;
    display: flex;
    align-items: center;
    justify-content: center;
}
.category-card .mini-search img {
    width: 10px;
    height: 10px;
    object-fit: contain;
}
.category-image {
    width: 76px;
    height: 76px;
    object-fit: contain;
    margin-top: 8px;
    margin-bottom: 12px;
}
.category-card h3 {
    font-size: 13px;
    font-weight: 700;
    color: #202230;
    margin: 0 0 8px 0;
    line-height: 1.3;
    min-height: 34px;
}
.lines-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.category-card p {
    font-size: 9px;
    color: #8a8e99;
    margin: 0;
    line-height: 1.4;
}
.text-link {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ff4569;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    margin-top: 16px;
    gap: 4px;
}
.text-link .arrow {
    font-family: inherit;
}

/* Brands Section */
.brands-section {
    padding: 40px 20px;
    background: #fff;
    border-top: 1px solid #f2f2f2;
}
.brand-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 16px;
}
.brand-card {
    background: #fff;
    border: 1px solid #f0f2f5;
    border-radius: 8px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
}
.brand-card img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
.brand-note {
    font-size: 9px;
    line-height: 1.4;
    color: #858895;
    text-align: center;
    background: #f9f9f9;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
}

/* Compliance & Protection */
.compliance-section {
    background: #000;
    padding: 40px 20px;
    color: #fff;
}
.compliance-inner {
    width: 100%;
}
.compliance-intro h2 {
    font-size: 22px;
    font-weight: 800;
    color: #fff;
    margin: 0 0 10px 0;
}
.compliance-intro p {
    font-size: 13px;
    color: #a4acba;
    line-height: 1.6;
    margin: 0 0 20px 0;
}
.compliance-intro .primary-btn {
    margin-bottom: 32px;
    height: 40px;
    font-size: 13px;
    max-width: 130px;
}
.compliance-cards-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.compliance-card {
    background: #121922;
    border-radius: 12px;
    padding: 20px;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    transition: background-color 0.2s;
}
.compliance-card:hover {
    background: #19222d;
}
.card-top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}
.compliance-card img {
    width: 24px;
    height: 24px;
    object-fit: contain;
}
.compliance-card h3 {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    margin: 0;
}
.compliance-card p {
    font-size: 12px;
    color: #a8afbb;
    line-height: 1.5;
    margin: 0 0 16px 0;
}
.compliance-card span {
    color: #ff4569;
    font-size: 12px;
    font-weight: 700;
}

/* Related Policies */
.related-section {
    padding: 32px 20px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-bottom: 1px solid #f2f2f2;
}
.related-section strong {
    font-size: 14px;
    font-weight: 800;
    color: #262937;
}
.policy-links {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.policy-link-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none;
    padding: 12px 14px;
    background: #f8f9fa;
    border-radius: 8px;
    color: #5f6472;
    font-size: 12px;
    font-weight: 500;
    transition: background-color 0.2s;
}
.policy-link-item:hover {
    background: #f1f3f5;
}
.policy-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.policy-left img {
    width: 16px;
    height: 16px;
    object-fit: contain;
}
.policy-link-item .chevron {
    color: #9ca1ae;
    font-size: 14px;
}
</style>
